<template>
  <div
    v-if="user && user.personalProfile"
    data-testid="personal-data"
    class="personal-data"
  >
    <InputLabelField
      v-model="user.name"
      label="Apelido"
      name="name"
      :required="true"
    />
    <ProfessionInput
      data-testid="profession-input"
      :profession="user.personalProfile.profession"
      :cbo="user.personalProfile.professionId"
      :error="professionInputErrorMessage"
      @on-select="setProfession"
    />
    <CheckboxRadio
      v-model="user.personalProfile.hasProfession"
      :labels="['Estou trabalhando', 'Não estou trabalhando']"
      :values="[true, false]"
      name="hasProfession"
      class="mb"
      @input="changeHasProfession"
    />
    <div
      v-if="user.personalProfile.hasProfession"
      class="personal-data__employed-form user-account__row-column"
    >
      <InputLabelField
        v-model="user.personalProfile.workOrganization"
        name="workOrganization"
        label="Instituição onde trabalha"
        :required="true"
      />
      <InputLabelField
        v-model="user.personalProfile.workOrganizationCnpj"
        name="workOrganizationCnpj"
        label="CNPJ da empresa"
        :required="true"
        :mask="cnpjmask"
      />
    </div>
    <InputSelect
      id="marital-status"
      v-model="user.personalProfile.maritalStatus"
      class="mb"
      name="maritalStatus"
      :label="getString('common_field_maritalstatus_label')"
      :options="getEnumOptions('MaritalStatus')"
      :required="true"
      @change="changeMaritalStatus"
    />
    <template v-if="!isSingle">
      <InputLabelField
        v-model="user.personalProfile.partnerName"
        name="partnerName"
        label="Nome da(o) companheira(o)"
        :required="true"
      />
      <InputLabelField
        v-if="enablePartnerCpf"
        v-model="user.personalProfile.partnerCpf"
        name="partnerCpf"
        label="CPF da(o) companheira(o)"
        :required="true"
        :mask="cpfmask"
      />
      <InputText
        v-else-if="enabledPartnerRne"
        v-model="user.personalProfile.partnerForeignRne"
        class="mb"
        name="partnerForeignRne"
        label="RNE da(o) companheira(o)"
        :mask="rneMask"
      />
    </template>

    <InputSelect
      id="education"
      v-model="user.personalProfile.education"
      class="mb"
      name="education"
      :label="getString('common_field_education_label')"
      :options="getEnumOptions('Education')"
      :required="true"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Emit } from 'vue-property-decorator';
import { BaseText, InputSelect, InputText } from '@warrenbrasil/nebraska-web';
import { getString, getEnumOptions } from '@/modules/common/helpers/resources';

import {
  ProfessionInput,
  ProfessionList
} from '@/modules/common/components/ProfessionInput';
import { CheckboxRadio } from '../CheckboxRadio';
import { InputLabelField } from '@/modules/account/components/InputLabelField';

@Component({
  components: {
    BaseText,
    CheckboxRadio,
    InputLabelField,
    InputSelect,
    ProfessionInput,
    InputText
  }
})
export default class PersonalDataTab extends Vue {
  @Prop({ required: false, default: false })
  private hasRegulationUpdate!: boolean;

  @Prop({ required: true }) private user!: any;

  private getEnumOptions = getEnumOptions;
  private getString = getString;

  private readonly cnpjmask = '##.###.###/####-##';
  private readonly cpfmask = '###.###.###-##';
  private readonly rneMask = 'X######-X';

  private get enablePartnerCpf() {
    switch (this.user.personalProfile.maritalStatus) {
      case 'casado-brasileiro-nato':
      case 'casado-brasileiro-naturalizado':
      case 'uniao-estavel':
        return true;
      default:
        return false;
    }
  }

  private get enabledPartnerRne() {
    return this.user.personalProfile.maritalStatus === 'casado-estrangeiro';
  }

  private get isSingle() {
    return /desquitado|divorciado|solteiro|viuvo/.test(
      this.user.personalProfile.maritalStatus
    );
  }

  private created() {
    this.validateProfession();
  }

  @Emit('validate-field')
  private validateProfession() {
    return {
      field: 'profession',
      isValid: !!this.user?.personalProfile?.profession
    };
  }

  private setProfession(option: ProfessionList) {
    this.user.personalProfile.profession = option.profession;
    this.user.personalProfile.professionId = option.cbo;
    this.validateProfession();
  }

  public changeHasProfession() {
    if (!this.user.personalProfile.hasProfession) {
      this.user.personalProfile.workOrganization = '';
      this.user.personalProfile.workOrganizationCnpj = '';
    }
  }

  public changeMaritalStatus() {
    if (this.isSingle) {
      this.user.personalProfile.partnerCpf = '';
      this.user.personalProfile.partnerName = '';
      this.user.personalProfile.partnerForeignRne = '';
    }

    if (this.enablePartnerCpf) {
      this.user.personalProfile.partnerForeignRne = '';
    }

    if (this.enabledPartnerRne) {
      this.user.personalProfile.partnerCpf = '';
    }
  }

  public get professionInputErrorMessage() {
    if (!this.user.personalProfile.profession) {
      return 'Selecione uma profissão';
    }

    if (!this.user.personalProfile.professionId) {
      return 'Atualizamos o campo de ocupação. Por favor, selecione sua ocupação atual.';
    }

    return '';
  }
}
</script>
<style lang="less">
.mb {
  margin-bottom: @size-spacing-x500;
}
</style>
